<template>
	<v-menu
		:close-on-content-click="false"
		offset-y
		left
		nudge-bottom="22"
		:elevation="$vuetify.theme.dark ? 9 : 8"
		content-class="list-style notification-menu-content"
	>
		<template #activator="{ on, attrs }">
			<!-- Quando não há notificações (total = 0) -->
			<div v-if="total === 0">
				<v-icon
					v-bind="attrs"
					v-on="on"
				>
					{{ icons.mdiBellOutline }}
				</v-icon>
			</div>

			<!-- Quando há notificações (total > 0) -->
			<div v-else>
				<!-- A prop :content exibe o número no badge -->
				<v-badge
					top
					color="primary"
					overlap
					offset-x="10"
					offset-y="10"
					:content="total"
				>
					<v-icon
						v-bind="attrs"
						v-on="on"
					>
						{{ icons.mdiBellOutline }}
					</v-icon>
				</v-badge>
			</div>
		</template>

		<v-card class="app-bar-notification-content-container">
			<perfect-scrollbar
				class="ps-user-notifications"
				:options="perfectScrollbarOptions"
			>
				<v-list class="py-0">
					<!-- Header -->

					<v-list-item
						class="d-flex"
						link
					>
						<div class="d-flex align-center justify-space-between flex-grow-1">
							<span class="font-weight-semibold">{{ $t('notifications.notifications') }}</span>

							<div
								key="read-all-btn"
								class="read-all-btn-list-item small"
								block
								:disabled="notificationStatus === 'pending'"
								color="primary"
								@click="markNotificationRealAll"
							>
								{{ $t('notifications.allRead') }}
							</div>

							<v-chip
								class="primary--text font-weight-semibold"
								small
							>
								{{ total }} {{ $t(total === 1 ? 'notifications.new' : 'notifications.news') }}
							</v-chip>
						</div>
					</v-list-item>
					<v-divider />

					<div v-if="notifications">
						<!-- Notifications -->
						<template v-for="(notification, index) in notifications.data">
							<v-list-item
								:key="notification.id"
								:class="{ 'notification-read': notification.read_at !== null }"
								link
								@click="markNotificationRead(notification)"
							>
								<!-- Avatar -->
								<v-list-item-avatar
									:class="[
										{
											'v-avatar-light-bg primary--text justify-center':
												notification.user && !notification.user.avatar,
										},
									]"
									size="38"
								>
									<v-img
										v-if="notification.data.image"
										:src="notification.data.image"
									/>
								</v-list-item-avatar>

								<!-- Content -->
								<v-list-item-content class="d-block">
									<v-list-item-title class="text-sm font-weight-semibold">
										{{ getTitle(notification) }}
									</v-list-item-title>
									<v-list-item-subtitle class="text-sm">
										{{ getMessage(notification) }}
									</v-list-item-subtitle>
								</v-list-item-content>

								<!-- Item Action/Time -->
								<v-list-item-action>
									<span class="text--secondary text-xs">{{ notification.created_at_diff }}</span>
								</v-list-item-action>
							</v-list-item>
							<v-divider :key="index" />
						</template>
					</div>

					<!-- <v-list-item key="read-all-btn" class="read-all-btn-list-item">
						<v-btn block :disabled="notificationStatus === 'pending'" color="primary" @click="markNotificationRealAll">
							{{ $t('notifications.allRead') }}
						</v-btn>
						<v-progress-circular indeterminate color="green" />
					</v-list-item> -->

					<v-list-item v-if="notificationStatus === 'pending'">
						<v-progress-circular
							indeterminate
							color="green"
						/>
					</v-list-item>

					<!-- Auto-loads the next page as soon as this row is visible. The
							dropdown is short enough (400px, 5 per page) that it rarely
							overflows on its own, so v-intersect fires immediately and
							keeps pulling pages without the user having to scroll/drag. -->
					<v-list-item
						v-else-if="hasMoreNotifications"
						v-intersect="onLoadMoreIntersect"
						style="justify-content: center"
					>
						<v-progress-circular
							indeterminate
							size="20"
							width="2"
							color="primary"
						/>
					</v-list-item>
				</v-list>
			</perfect-scrollbar>
			<v-list-item
				v-if="notifications && notifications.data && notifications.data.length"
				style="justify-content: center"
			>
				<router-link to="/mundo-gamer-platforms/notifications">
					<v-btn text>
						{{ $t('notifications.seeAll') }}
					</v-btn>
				</router-link>
			</v-list-item>
		</v-card>
	</v-menu>
</template>

<script>
import { generateAdminRoute } from '@/utils/adminUrlGenerator'
import { getInitialName } from '@core/utils'
import { mdiBellOutline } from '@mdi/js'

// 3rd Party
import _ from 'lodash'
import { PerfectScrollbar } from 'vue2-perfect-scrollbar'
import { mapActions, mapGetters } from 'vuex'

export default {
	components: {
		PerfectScrollbar,
	},
	setup() {
		const perfectScrollbarOptions = {
			maxScrollbarLength: 60,
			wheelPropagation: false,
		}

		return {
			getInitialName,
			perfectScrollbarOptions,
			icons: {
				mdiBellOutline,
			},
		}
	},
	data() {
		return {
			// shortName da plataforma usada no canal privado do Echo
			platformShortName: process.env.VUE_APP_PLATFORM_SHORT_NAME || 'MGN',
		}
	},
	computed: {
		...mapGetters({
			notifications: 'main/notifications/getNotificationList',
			notificationStatus: 'main/notifications/getNotificationStatus',
			notificationTotalLatest: 'main/notifications/getNotificationTotalLatest',
		}),
		total() {
			return (this.notificationTotalLatest && this.notificationTotalLatest.total) || 0
		},
		adminBaseUrl() {
			return process.env.VUE_APP_ADMIN_BASE_URL || 'http://localhost:8080'
		},
		hasMoreNotifications() {
			const pagination = this.notifications && this.notifications.meta && this.notifications.meta.pagination
			return !!pagination && pagination.current_page < pagination.total_pages
		},
	},
	async mounted() {
		await this.loadInitialNotifications()
		this.subscribeToEcho()
	},
	beforeDestroy() {
		this.unsubscribeFromEcho()
	},
	methods: {
		...mapActions({
			fetchNotifications: 'main/notifications/fetchNotifications',
			fetchMoreNotifications: 'main/notifications/fetchMoreNotifications',
			fetchNotificationsTotalLatest: 'main/notifications/fetchNotificationsTotalLatest',
			markRead: 'main/notifications/markNotificationRead',
			markReadAll: 'main/notifications/markNotificationsReadAll',
			prependNotification: 'main/notifications/prependNotification',
		}),
		// IntersectionObserver (via v-intersect) accounts for the
		// perfect-scrollbar's own clipping, so this fires both when the row
		// is visible right away (short list) and when scrolling brings it
		// back into view (long list) — no click needed either way.
		onLoadMoreIntersect(entries) {
			if (entries[0] && entries[0].isIntersecting) this.loadMoreNotifications()
		},
		async loadMoreNotifications() {
			if (this.notificationStatus !== 'init' || !this.hasMoreNotifications) return

			const pagination = this.notifications.meta.pagination
			try {
				await this.fetchMoreNotifications({
					sort: 'created_at',
					order: 'desc',
					per_page: 5,
					page: pagination.current_page + 1,
				})
			} catch (error) {
				console.error('Erro ao carregar mais notificações:', error)
			}
		},
		async markNotificationRead(notification) {
			await this.markRead({ notificationId: notification.id })

			const { object, id, url } = notification.data
			// Prefer the backend's own url: it already knows the exact deep link
			// (e.g. a submission id as a query param) and is the same value the
			// e-mail's "Review" button uses. generateAdminRoute()'s static map is
			// a fallback for older notifications persisted before their type
			// carried a url — every new notification type used to need a matching
			// entry here first, and until it shipped the bell click was a silent
			// dead end (see the comments in adminUrlGenerator.js).
			const route = url || generateAdminRoute(object, id)

			if (route) {
				// notification.data.url isn't always relative — api-main's
				// NotificationDestinationResolver already absolutizes it
				// server-side for some object types (e.g. showcase_media_partner_
				// application, which has no OBJECT_DESTINATIONS entry there but
				// matches its isAdminPath() check). Prepending adminBaseUrl again
				// on top of an already-absolute url produced a doubled, broken
				// domain (https://admin.mundogamer.networkhttps://admin.mundogamer.network/...).
				const fullUrl = route.startsWith('http') ? route : `${this.adminBaseUrl}${route}`
				if (fullUrl.startsWith('http')) {
					window.location.href = fullUrl
				} else {
					this.$router.push(fullUrl)
				}
			} else {
				console.error('Falha ao gerar a URL:', notification)
			}

			await this.fetchNotifications({ sort: 'created_at', order: 'desc', per_page: 5, page: 1 })
			await this.fetchNotificationsTotalLatest({ 'filter[read]': 0, total: 1 })
		},
		async markNotificationRealAll() {
			try {
				await this.markReadAll()

				await Promise.all([
					this.fetchNotifications({ sort: 'created_at', order: 'desc', per_page: 5, page: 1 }),
					this.fetchNotificationsTotalLatest({ 'filter[read]': 0, total: 1 }),
				])
			} catch (error) {
				console.error('Erro ao marcar todas as notificações como lidas:', error)
			} finally {
				this.notificationStatus = 'init'
			}
		},
		// Carrega as notificações iniciais via HTTP (único momento em que fazemos requisição na montagem)
		async loadInitialNotifications() {
			await Promise.all([
				this.fetchNotifications({ sort: 'created_at', order: 'desc', per_page: 5, page: 1 }),
				this.fetchNotificationsTotalLatest({ 'filter[read]': 0, total: 1 }),
			])
		},
		// Assina o canal privado via Laravel Echo — sem polling, sem setInterval
		subscribeToEcho() {
			if (!this.$echo) return
			const user = this.$authService.getUser()
			const channel = `user.${this.platformShortName}.${user.uuid}`

			this.$echo.private(channel).notification(notification => {
				this.prependNotification(notification)
			})
		},
		// Remove a assinatura ao destruir o componente
		unsubscribeFromEcho() {
			if (!this.$echo) return
			const user = this.$authService.getUser()
			const channel = `user.${this.platformShortName}.${user.uuid}`
			this.$echo.leaveChannel(`private-${channel}`)
		},
		getTitle(notification) {
			return _.get(notification, 'data.title', '')
		},
		getMessage(notification) {
			return _.get(notification, 'data.message', '')
		},
	},
}
</script>

<style lang="scss" scoped>
.notification-read {
	opacity: 0.6;
}

// Vuetify's default .v-list-item__title/__subtitle are single-line
// (nowrap + ellipsis) — that silently re-truncates the full text we now
// pass in from getTitle()/getMessage() unless overridden here.
::v-deep .app-bar-notification-content-container {
	.v-list-item__title,
	.v-list-item__subtitle {
		white-space: normal;
		overflow: visible;
		text-overflow: unset;

		// Messages carry e-mails and URLs (e.g. the account-deletion request).
		// With overflow visible those have no break opportunity and would
		// spill outside the dropdown instead of wrapping.
		overflow-wrap: anywhere;
	}
}

.app-bar-notification-content-container {
	// The menu is absolutely positioned with width:auto, so it shrink-to-fits
	// to its max-content. Once the title/message stopped being nowrap+ellipsis
	// the longest *message* started driving that width and the dropdown
	// stretched across the whole screen. Capping the card keeps it a normal
	// dropdown: the title still renders whole, the message just wraps.
	//
	// 560px and not less: the row spends ~186px on chrome (32 padding +
	// 38 avatar + 16 margin + ~84 timestamp + 16 margin), leaving ~374px of
	// text at .text-sm (14px) — about 53 characters per line, so titles fit
	// on one line and the longest messages wrap to two. At 400px the text
	// column drops to ~30 characters and messages ran to three lines.
	// Ceiling for reference: AppBarContentPublisher uses max-width 600.
	max-width: 560px;

	@media #{map-get($display-breakpoints, 'xs-only')} {
		max-width: 100%;
	}

	// Narrower card squeezes the flex row — keep the relative timestamp on a
	// single line instead of breaking into "11 minutes / ago".
	::v-deep .v-list-item__action {
		white-space: nowrap;
	}

	.read-all-btn-list-item {
		font-size: 10px;
		padding: 2px 8px;
		margin: 0 2px;
		border-radius: 10px;
		cursor: pointer;
		color: #fff;
		background-color: #5a595991;
		&:hover {
			background-color: var(--v-primary-base);
		}
	}
}

.ps-user-notifications {
	max-height: 400px;
	overflow-y: auto;
}

.notification-menu-content {
	@media #{map-get($display-breakpoints, 'xs-only')} {
		min-width: calc(100vw - (1.5rem * 2)) !important;
		left: 50% !important;
		transform: translateX(-50%);
	}
}

.v-progress-circular {
	width: 100% !important;
}

/* width */
::-webkit-scrollbar {
	width: 5px;
}
/* Track */
::-webkit-scrollbar-track {
	box-shadow: inset 0 0 5px rgba(166, 166, 166, 0);
	border-radius: 0px 0px 0px 0px;
	background: transparent;
}
/* Handle */
::-webkit-scrollbar-thumb {
	background: gray;
	border-radius: 0px 0px 0px 0px;
}
/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
	background: #ee3831;
}
</style>
